import React, { useState, useEffect, useCallback, useRef } from 'react';
import { showToast } from '../utils/toast';
import {
  FiPlus, FiSearch, FiChevronDown, FiChevronRight,
  FiArrowRight, FiTrash2, FiX, FiCheck, FiSlash,
} from 'react-icons/fi';
import api from '../services/api';
import { fmtFecha, fmtCant, localDate } from '../utils/format';
import '../styles/Inventarios.css';

const hoy = () => localDate();

const ESTADO_META = {
  BORRADOR:   { label: 'BORRADOR',   cls: 'est-borrador' },
  COMPLETADA: { label: 'COMPLETADA', cls: 'est-completada' },
  ANULADA:    { label: 'ANULADA',    cls: 'est-anulada' },
};

/* ── Modal nueva transferencia ─────────────────────────────────── */
function ModalTransferencia({ almacenes, onClose, onSaved }) {
  const EMPTY = { almacen_origen_id: '', almacen_destino_id: '', fecha: hoy(), observacion: '' };
  const [form,   setForm]   = useState(EMPTY);
  const [lineas, setLineas] = useState([]);
  const [busq,   setBusq]   = useState('');
  const [res,    setRes]    = useState([]);
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const searchRef = useRef(null);

  const buscarTimer = useRef(null);
  const buscar = (v) => {
    setBusq(v);
    clearTimeout(buscarTimer.current);
    buscarTimer.current = setTimeout(async () => {
      if (!v.trim()) { setRes([]); return; }
      try { const r = await api.get('/api/productos', { params: { q: v } }); setRes((r.data?.data||[]).slice(0,8)); }
      catch { setRes([]); }
    }, 250);
  };

  const agregar = (p) => {
    if (lineas.find(l => l.producto_id === p.id)) { setBusq(''); setRes([]); return; }
    setLineas(prev => [...prev, {
      producto_id: p.id, producto_nombre: p.nombre, codigo: p.codigo,
      unidad_short: p.unidad_minima_short || p.unidad_minima_nombre || 'UND',
      cantidad: '',
    }]);
    setBusq(''); setRes([]);
    searchRef.current?.focus();
  };

  const setLinea = (idx, val) => setLineas(p => p.map((l,i) => i===idx ? {...l, cantidad: val} : l));
  const quitar   = (idx) => setLineas(p => p.filter((_,i) => i!==idx));

  const guardar = async () => {
    setErr('');
    if (form.almacen_origen_id === form.almacen_destino_id && form.almacen_origen_id) {
      setErr('Origen y destino no pueden ser el mismo almacén'); return;
    }
    for (const l of lineas) {
      if (!l.cantidad || parseFloat(l.cantidad) <= 0) {
        setErr(`Cantidad inválida para "${l.producto_nombre}"`); return;
      }
    }
    setSaving(true);
    try {
      await api.post('/api/transferencias', {
        ...form,
        lineas: lineas.map(l => ({ producto_id: l.producto_id, cantidad: parseFloat(l.cantidad) })),
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="inv-modal-overlay">
      <div className="inv-modal inv-modal-lg">
        <div className="inv-modal-header">
          <span>Nueva Transferencia</span>
          <button className="inv-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="inv-modal-body">
          <div className="inv-form-grid">
            <div className="inv-field">
              <label>Almacén Origen *</label>
              <select className="inv-select" value={form.almacen_origen_id}
                onChange={e => setForm(p => ({ ...p, almacen_origen_id: e.target.value }))}>
                <option value="">— seleccione —</option>
                {almacenes.filter(a=>a.activo).map(a => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </div>
            <div className="inv-field inv-field-arrow">
              <FiArrowRight size={20} color="#94a3b8" />
            </div>
            <div className="inv-field">
              <label>Almacén Destino *</label>
              <select className="inv-select" value={form.almacen_destino_id}
                onChange={e => setForm(p => ({ ...p, almacen_destino_id: e.target.value }))}>
                <option value="">— seleccione —</option>
                {almacenes.filter(a=>a.activo && String(a.id) !== String(form.almacen_origen_id)).map(a => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </div>
            <div className="inv-field">
              <label>Fecha *</label>
              <input type="date" className="inv-input" value={form.fecha}
                onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} />
            </div>
            <div className="inv-field inv-field-full">
              <label>Observación</label>
              <input className="inv-input" value={form.observacion}
                onChange={e => setForm(p => ({ ...p, observacion: e.target.value }))}
                placeholder="Opcional" />
            </div>
          </div>

          <div className="inv-lineas-header">
            <span>Productos a transferir</span>
            <div className="inv-search-prod" style={{ position: 'relative' }}>
              <FiSearch size={12} />
              <input ref={searchRef} placeholder="Buscar producto..."
                value={busq} onChange={e => buscar(e.target.value)} autoComplete="off" />
              {res.length > 0 && (
                <ul className="inv-prod-dropdown">
                  {res.map(p => (
                    <li key={p.id} onClick={() => agregar(p)}>
                      <span className="inv-pd-nombre">{p.nombre}</span>
                      <span className="inv-pd-cod">{p.codigo||''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <table className="inv-lineas-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Unidad</th>
                <th className="inv-th-cant">Cantidad</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lineas.length === 0 ? (
                <tr><td colSpan={4} className="inv-lineas-empty">Agregue los productos a transferir</td></tr>
              ) : lineas.map((l, i) => (
                <tr key={l.producto_id}>
                  <td>
                    <span className="inv-linea-nombre">{l.producto_nombre}</span>
                    {l.codigo && <span className="inv-linea-cod">{l.codigo}</span>}
                  </td>
                  <td>{l.unidad_short}</td>
                  <td>
                    <input type="number" min="0.01" step="0.01" className="inv-input-cant"
                      value={l.cantidad} onChange={e => setLinea(i, e.target.value)}
                      autoFocus={i === lineas.length - 1} />
                  </td>
                  <td>
                    <button className="inv-btn-del" onClick={() => quitar(i)}><FiTrash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {err && <div className="inv-modal-error">{err}</div>}
        <div className="inv-modal-footer">
          <button className="inv-btn-save" onClick={guardar}
            disabled={saving || !form.almacen_origen_id || !form.almacen_destino_id || lineas.length === 0}>
            {saving ? 'Guardando...' : 'Crear Transferencia'}
          </button>
          <button className="inv-btn-cancel" onClick={onClose}>× Salir</button>
        </div>
      </div>
    </div>
  );
}

/* ── Fila expandible ───────────────────────────────────────────── */
function FilaTransferencia({ tra, onRefresh }) {
  const [open,    setOpen]    = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleOpen = async () => {
    if (!open && !detalle) {
      setLoading(true);
      try { const r = await api.get(`/api/transferencias/${tra.id}`); setDetalle(r.data); }
      catch { setDetalle({ lineas: [] }); }
      finally { setLoading(false); }
    }
    setOpen(!open);
  };

  const accion = async (tipo) => {
    const msg = tipo === 'completar'
      ? '¿Confirma completar esta transferencia? El stock se actualizará.'
      : '¿Anular esta transferencia?';
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/api/transferencias/${tra.id}/${tipo}`);
      onRefresh();
    } catch (e) { showToast(e.response?.data?.error || e.message); }
  };

  const meta = ESTADO_META[tra.estado] || ESTADO_META.BORRADOR;

  return (
    <>
      <tr className="inv-row" onClick={toggleOpen} style={{ cursor: 'pointer' }}>
        <td className="inv-td inv-td-icon">
          {open ? <FiChevronDown size={13}/> : <FiChevronRight size={13}/>}
        </td>
        <td className="inv-td inv-td-fecha">{fmtFecha(tra.fecha)}</td>
        <td className="inv-td inv-td-num">{tra.numero}</td>
        <td className="inv-td">
          <span className="inv-almacen-flow">
            {tra.origen_nombre} <FiArrowRight size={11}/> {tra.destino_nombre}
          </span>
        </td>
        <td className="inv-td">
          <span className={`inv-estado-badge ${meta.cls}`}>{meta.label}</span>
        </td>
        <td className="inv-td inv-td-items">{tra.nro_items}</td>
        <td className="inv-td inv-td-obs">{tra.observacion || '—'}</td>
        <td className="inv-td inv-td-acciones" onClick={e => e.stopPropagation()}>
          {tra.estado === 'BORRADOR' && (
            <div className="ia-actions">
              <button className="ia-btn ia-btn--complete" onClick={() => accion('completar')} title="Completar">
                <FiCheck size={15}/>
              </button>
              <button className="ia-btn ia-btn--danger" onClick={() => accion('anular')} title="Anular">
                <FiSlash size={15}/>
              </button>
            </div>
          )}
        </td>
      </tr>
      {open && (
        <tr className="inv-row-detail">
          <td colSpan={8}>
            {loading ? <span style={{padding:'8px 16px',color:'#64748b'}}>Cargando...</span> : (
              <table className="inv-detail-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Código</th>
                    <th>Unidad</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {(detalle?.lineas||[]).map(l => (
                    <tr key={l.id}>
                      <td>{l.producto_nombre}</td>
                      <td className="inv-td-cod">{l.codigo||'—'}</td>
                      <td>{l.unidad_short||l.unidad_nombre||'—'}</td>
                      <td className="inv-td-cant">{fmtCant(l.cantidad)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Página principal ──────────────────────────────────────────── */
export default function TransferenciasPage() {
  const [trans,    setTrans]   = useState([]);
  const [almacenes, setAlms]   = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState('');
  const [modal,    setModal]   = useState(false);
  const [filtros,  setFiltros] = useState({ almacen_id: '', estado: '', desde: '', hasta: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [rt, ra] = await Promise.all([
        api.get('/api/transferencias', { params: filtros }),
        api.get('/api/almacenes'),
      ]);
      setTrans(rt.data?.data || []);
      setAlms(ra.data?.data  || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="inv-page">
      <div className="inv-toolbar">
        <button className="inv-btn-nuevo" onClick={() => setModal(true)}>
          <FiPlus /> Nueva Transferencia
        </button>
        <select className="inv-filter-select" value={filtros.almacen_id}
          onChange={e => setFiltros(p => ({ ...p, almacen_id: e.target.value }))}>
          <option value="">Todos los almacenes</option>
          {almacenes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <select className="inv-filter-select" value={filtros.estado}
          onChange={e => setFiltros(p => ({ ...p, estado: e.target.value }))}>
          <option value="">Todos los estados</option>
          <option value="BORRADOR">Borrador</option>
          <option value="COMPLETADA">Completadas</option>
          <option value="ANULADA">Anuladas</option>
        </select>
        <input type="date" className="inv-filter-date" value={filtros.desde}
          onChange={e => setFiltros(p => ({ ...p, desde: e.target.value }))} title="Desde" />
        <span className="inv-filter-sep">→</span>
        <input type="date" className="inv-filter-date" value={filtros.hasta}
          onChange={e => setFiltros(p => ({ ...p, hasta: e.target.value }))} title="Hasta" />
        {(filtros.almacen_id || filtros.estado || filtros.desde || filtros.hasta) && (
          <button className="inv-btn-clear"
            onClick={() => setFiltros({ almacen_id:'', estado:'', desde:'', hasta:'' })}>
            <FiX size={13}/> Limpiar
          </button>
        )}
      </div>

      {loading ? <div className="inv-loading">Cargando transferencias...</div>
       : error   ? <div className="inv-error">{error}</div>
       : (
        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th></th>
                <th>Fecha</th>
                <th>Número</th>
                <th>Movimiento</th>
                <th>Estado</th>
                <th>Items</th>
                <th>Observación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {trans.length === 0
                ? <tr><td colSpan={8} className="inv-empty">Sin transferencias registradas</td></tr>
                : trans.map(t => <FilaTransferencia key={t.id} tra={t} onRefresh={load} />)}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ModalTransferencia
          almacenes={almacenes}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); }}
        />
      )}
    </div>
  );
}
